import { useEffect, useRef } from 'react';
import { dispatchMilestone } from '@/components/milestones/MilestoneCelebration';
import { getLevelName, type UserProgress } from '@/hooks/useProgress';
import { learningPaths } from '@/data/lessons';
import { soccerLearningPaths } from '@/data/soccerLessons';
import { basketballLearningPaths } from '@/data/basketballLessons';
import { baseballLearningPaths } from '@/data/baseballLessons';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100];

function allPaths() {
  return [...learningPaths, ...soccerLearningPaths, ...basketballLearningPaths, ...baseballLearningPaths];
}

function seenKey(userId: string, kind: string, id: string) {
  return `milestone_seen_${userId}_${kind}_${id}`;
}

function markSeen(userId: string, kind: string, id: string) {
  try { localStorage.setItem(seenKey(userId, kind, id), '1'); } catch {}
}

function hasSeen(userId: string, kind: string, id: string) {
  try { return localStorage.getItem(seenKey(userId, kind, id)) === '1'; } catch { return false; }
}

export function useMilestoneWatchers(progress: UserProgress, loaded: boolean) {
  const { user } = useAuth();
  const prevRef = useRef<UserProgress | null>(null);
  const baselineSetRef = useRef(false);
  const leaderboardCheckedRef = useRef(false);

  // Establish baseline on first loaded snapshot — so reload doesn't refire history
  useEffect(() => {
    if (!loaded || !user) return;
    if (!baselineSetRef.current) {
      // Mark every already-true milestone as seen so we only fire on NEW ones
      // Level
      markSeen(user.id, 'level', String(progress.level));
      // Streak: mark the highest passed milestone as seen
      for (const m of STREAK_MILESTONES) {
        if (progress.streak >= m) markSeen(user.id, 'streak', String(m));
      }
      // First lesson
      if (progress.completedLessons.length >= 1) markSeen(user.id, 'first-lesson', 'first');
      // Modules already done
      for (const path of allPaths()) {
        for (const unit of path.units) {
          const allDone = unit.lessons.every(l => progress.completedLessons.includes(l.id));
          if (allDone) markSeen(user.id, 'module', unit.id || `${path.id}-${unit.title}`);
        }
      }
      baselineSetRef.current = true;
      prevRef.current = progress;
      return;
    }
  }, [loaded, user, progress]);

  // Watch for changes
  useEffect(() => {
    if (!loaded || !user || !baselineSetRef.current) return;
    const prev = prevRef.current;
    prevRef.current = progress;
    if (!prev) return;

    // First lesson ever
    if (prev.completedLessons.length === 0 && progress.completedLessons.length >= 1
        && !hasSeen(user.id, 'first-lesson', 'first')) {
      markSeen(user.id, 'first-lesson', 'first');
      dispatchMilestone({
        id: `first-lesson-${user.id}`,
        kind: 'first-lesson',
        emoji: '🎯',
        headline: 'First Lesson Complete!',
        subline: "You're officially on the path. The journey starts here.",
        xp: progress.xp,
      });
    }

    // Level up
    if (progress.level > prev.level && !hasSeen(user.id, 'level', String(progress.level))) {
      markSeen(user.id, 'level', String(progress.level));
      dispatchMilestone({
        id: `level-${progress.level}`,
        kind: 'level-up',
        emoji: '🏆',
        headline: 'Level Up!',
        subline: `You reached ${getLevelName(progress.level)} — keep climbing!`,
        xp: progress.xp,
      });
    }

    // Streak milestone
    for (const m of STREAK_MILESTONES) {
      if (progress.streak >= m && prev.streak < m && !hasSeen(user.id, 'streak', String(m))) {
        markSeen(user.id, 'streak', String(m));
        dispatchMilestone({
          id: `streak-${m}`,
          kind: 'streak',
          emoji: '🔥',
          headline: 'Streak on Fire!',
          subline: `${m}-day streak unlocked. You're unstoppable.`,
          xp: progress.xp,
          streakDays: m,
        });
        break;
      }
    }

    // Module complete
    if (progress.completedLessons.length > prev.completedLessons.length) {
      const justFinished = progress.completedLessons.filter(id => !prev.completedLessons.includes(id));
      for (const path of allPaths()) {
        for (const unit of path.units) {
          const containsNew = unit.lessons.some(l => justFinished.includes(l.id));
          if (!containsNew) continue;
          const allDone = unit.lessons.every(l => progress.completedLessons.includes(l.id));
          if (!allDone) continue;
          const uid = unit.id || `${path.id}-${unit.title}`;
          if (hasSeen(user.id, 'module', uid)) continue;
          markSeen(user.id, 'module', uid);
          dispatchMilestone({
            id: `module-${uid}`,
            kind: 'module-complete',
            emoji: '🎓',
            headline: 'Module Complete!',
            subline: `You finished "${unit.title}". On to the next challenge.`,
            xp: progress.xp,
          });
        }
      }
    }
  }, [progress, loaded, user]);

  // Leaderboard #1 — check once per session after load
  useEffect(() => {
    if (!loaded || !user || leaderboardCheckedRef.current) return;
    leaderboardCheckedRef.current = true;
    (async () => {
      try {
        const { data } = await supabase.rpc('get_leaderboard' as any, { result_limit: 1 });
        const top = Array.isArray(data) ? data[0] : null;
        if (top && (top.user_id === user.id) && !hasSeen(user.id, 'leaderboard-1', 'global')) {
          markSeen(user.id, 'leaderboard-1', 'global');
          dispatchMilestone({
            id: `leaderboard-1-${user.id}`,
            kind: 'leaderboard-1',
            emoji: '👑',
            headline: '#1 on the Leaderboard!',
            subline: "You're at the top. Defend your throne.",
            xp: progress.xp,
          });
        }
      } catch { /* leaderboard RPC optional */ }
    })();
  }, [loaded, user, progress.xp]);
}
